import { createContext, type FC, useMemo } from 'react';
import { useTheBoatDbApiFetcher } from '../fetchers/theboatdb-api-fetcher';
import { TanstackQueryApiClient } from '../rest-api-theboatdb/generated/tanstack.client';
import { type ApiClient, createApiClient } from '../rest-api-theboatdb/generated/theboatdb-typed-openapi';

/**
 * The typed client for TheBoatDB API, alongside — never instead of — the Laravel
 * one in `TypedOpenApiProvider`.
 *
 * Two clients rather than one with two base URLs, because they are two services:
 * different schemas, different generated types, different credentials and
 * different error envelopes. Sharing a client would mean a union of two
 * unrelated endpoint tables, and a call could then be typed against one API and
 * sent to the other.
 *
 * The context is `null` when no URL was supplied, which is the ordinary state for
 * an app that does not talk to TheBoatDB at all — `useTheBoatDbApi()` is what
 * turns that into a readable error, at the one call site that cares.
 */

type Props = {
  children: React.ReactNode;
  /** Origin of TheBoatDB API, e.g. `https://api.theboatdb.com`. */
  apiUrl?: string;
};

export type TheBoatDbApiState = {
  apiClient: ApiClient;
  tanstackApiClient: TanstackQueryApiClient;
} | null;

export const TheBoatDbApiContext = createContext<TheBoatDbApiState>(null);

export const TheBoatDbApiProvider: FC<Props> = ({ children, apiUrl }) => {
  const fetcher = useTheBoatDbApiFetcher();

  // Memoised on the URL and the fetcher rather than built once with `useState`:
  // the fetcher closes over `getToken`, and a client pinned to the first render's
  // copy would keep calling a stale one after the session changes.
  const value = useMemo<TheBoatDbApiState>(() => {
    if (!apiUrl) {
      return null;
    }

    const apiClient = createApiClient({ fetch: fetcher }, apiUrl);

    return { apiClient, tanstackApiClient: new TanstackQueryApiClient(apiClient) };
  }, [apiUrl, fetcher]);

  return <TheBoatDbApiContext.Provider value={value}>{children}</TheBoatDbApiContext.Provider>;
};
